import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardCheck,
  FileCheck2,
  ShieldCheck,
  TestTube2,
  Video,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import visitHero from "@/assets/visit-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const stepIcons = [ClipboardCheck, ShieldCheck, Video, TestTube2, FileCheck2] as const;

const SaFungerarDet = () => {
  const { t, lang } = useLang();
  const h = t.howItWorks;


  const startHref = lang === "sv" ? "/starta-arende" : "/en/start-case";
  const visitHref = lang === "sv" ? "/infor-besoket" : "/en/before-visit";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={h.metaTitle} description={h.metaDescription} />
      <Header />
      <main>
        <PageHeroCard
          imageSrc={visitHero}
          imageAlt={lang === "sv" ? "Läkarbesök" : "Doctor visit"}
          compact
        >
          <div className="animate-fade-up max-w-2xl">
            <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
              {h.eyebrow}
            </span>
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {h.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {h.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="xl" variant="hero">
                <Link to={startHref}>
                  {h.ctaPrimary}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="xl" variant="ghost-outline">
                <Link to={visitHref}>{h.ctaSecondary}</Link>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <ol className="space-y-4">
              {h.steps.map((step, i) => {
                const Icon = stepIcons[i] ?? ClipboardCheck;
                return (
                  <li
                    key={step.title}
                    className="flex flex-col gap-5 rounded-2xl border border-border/60 bg-card p-6 shadow-card sm:flex-row sm:items-start sm:gap-8 sm:rounded-3xl sm:p-8"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-display text-xl font-semibold text-foreground sm:text-2xl">
                        {step.title}
                      </h2>
                      <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                        {step.text}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        <section className="px-2 pb-16 sm:px-3 lg:px-4 lg:pb-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-primary-foreground">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {h.secureTitle}
              </h2>
              <p className="mt-3 text-base text-primary-foreground/85">{h.secureText}</p>
              <div className="mt-7">
                <Button asChild variant="onPrimary" size="xl">
                  <Link to={startHref}>
                    {h.ctaPrimary}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: h.eyebrow },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default SaFungerarDet;
